import {
    Box,
    Button,
    ButtonProps,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    FormControlLabel,
    Typography,
} from '@mui/material'
import { ReactElement, ReactNode, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface ConfirmPopupNotice {
    /** info = teal, for what stays or is undone; warning = amber, for what the action changes */
    tone: 'info' | 'warning'
    title?: ReactNode
    content: ReactNode
}

export interface ConfirmPopupProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    /** names the action only; counts go in `lead` */
    title: ReactNode
    /** the one sentence saying what is about to happen */
    lead?: ReactNode
    /** a quieter line introducing the summary box */
    intro?: ReactNode
    /** label/value rows restating what the action applies to */
    summary?: { label: string; value: ReactNode }[]
    notices?: ConfirmPopupNotice[]
    /** free-form body, rendered after the lead */
    children?: ReactNode
    /** when given, a tick box with this label must be checked before the primary button arms */
    acknowledgeLabel?: ReactNode
    acknowledgeId?: string
    confirmLabel: ReactNode
    /** defaults to "Cancel" */
    cancelLabel?: ReactNode
    confirmDisabled?: boolean
    /** shows a spinner on the primary button and disables it */
    loading?: boolean
    maxWidth?: DialogProps['maxWidth']
    fullWidth?: boolean
    /**
     * Rules above and below the body. On by default, where they separate a long body from the title
     * and buttons; turn off for a one- or two-line confirm, where they only box in a sentence.
     */
    dividers?: boolean
    /** ids (the E2E suite locates by id), title, autoFocus and the like; an `sx` here replaces ours */
    confirmButtonProps?: ButtonProps
    cancelButtonProps?: ButtonProps
}

/**
 * The one confirmation popup (UBQ-5808): title / lead / summary box / notice boxes / "I understand"
 * tick / Cancel + primary. Every part but the title is optional, so a plain "Delete this?" and a
 * bulk action with consequences share one layout. Title size, padding and corner radius come from
 * the theme's `MuiDialog*` defaults; colours from `palette.notice` and `palette.summary`.
 */
const ConfirmPopup = ({
    open,
    onClose,
    onConfirm,
    title,
    lead,
    intro,
    summary,
    notices,
    children,
    acknowledgeLabel,
    acknowledgeId,
    confirmLabel,
    cancelLabel,
    confirmDisabled,
    loading,
    maxWidth = 'sm',
    fullWidth = true,
    dividers = true,
    confirmButtonProps,
    cancelButtonProps,
}: ConfirmPopupProps): ReactElement => {
    const { t } = useTranslation()
    const titleId = useId()
    const [acknowledged, setAcknowledged] = useState(false)

    // A second run must not inherit the tick from the first.
    useEffect(() => {
        if (open) setAcknowledged(false)
    }, [open])

    const needsAcknowledge = acknowledgeLabel !== undefined
    const armed = !needsAcknowledge || acknowledged

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            aria-labelledby={titleId}
        >
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent dividers={dividers}>
                {lead && (
                    <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{ mb: 2 }}
                    >
                        {lead}
                    </Typography>
                )}
                {children}
                {intro && (
                    <Typography
                        component="div"
                        sx={{ color: 'text.secondary', mb: 1.5 }}
                    >
                        {intro}
                    </Typography>
                )}
                {summary && summary.length > 0 && (
                    <Box
                        sx={{
                            bgcolor: 'summary.background',
                            border: 1,
                            borderColor: 'summary.border',
                            borderRadius: 2,
                            p: '12px 14px',
                            mb: 1.75,
                            display: 'grid',
                            gridTemplateColumns: 'minmax(96px, auto) 1fr',
                            columnGap: 2,
                            rowGap: 0.75,
                            color: 'summary.ink',
                        }}
                    >
                        {summary.map((row) => (
                            <Box key={row.label} sx={{ display: 'contents' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 'fontWeightBold',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        pt: '2px',
                                    }}
                                >
                                    {row.label}
                                </Typography>
                                <Typography component="div">
                                    {row.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {notices?.map((notice, i) => (
                    <Box
                        key={i}
                        sx={{
                            bgcolor: `notice.${notice.tone}.background`,
                            border: 1,
                            borderColor: `notice.${notice.tone}.border`,
                            borderRadius: 2,
                            p: '12px 14px',
                            mb: 1.5,
                            color: 'summary.ink',
                        }}
                    >
                        {notice.title && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 'fontWeightBold',
                                    color: `notice.${notice.tone}.ink`,
                                    mb: 0.5,
                                }}
                            >
                                {notice.title}
                            </Typography>
                        )}
                        <Typography component="div">
                            {notice.content}
                        </Typography>
                    </Box>
                ))}
                {/* A raw Checkbox rather than Common/CheckboxInput: that one ellipsises its label to
                    a single line, and this sentence is the thing the user is agreeing to. */}
                {needsAcknowledge && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                id={acknowledgeId}
                                checked={acknowledged}
                                onChange={(e) =>
                                    setAcknowledged(e.target.checked)
                                }
                            />
                        }
                        label={acknowledgeLabel}
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                typography: 'body1',
                            },
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                    {...cancelButtonProps}
                >
                    {cancelLabel ?? t('Cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={!armed || loading || confirmDisabled}
                    startIcon={
                        loading ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : undefined
                    }
                    sx={{ textTransform: 'none' }}
                    {...confirmButtonProps}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ConfirmPopup
